import { useMemo } from "react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { formatForecast, roundForecast } from "~/utils/formatting"
import {
  DateFormatType,
  convertToStartOfWeekUTC,
  formatDate,
  getStartOfCurrentWeekUTC,
  getStartOfWeekDatesBetween,
} from "~/utils/datetime"
import { type IStockOutTimesDetail } from "~/models/stockOutTimes"
import { getForecastBetweenWeeks, padAndSumCumulativeArrays } from "~/utils/forecastUtils"
import SuperChart, { type DateChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType, DateType } from "~/constants/graphs"

const MILLISECONDS_IN_WEEK = 7 * 24 * 60 * 60 * 1000

interface Props {
  entries: { skuId: string; warehouseId: string }[]
  rawDetailMap: Map<string, IStockOutTimesDetail>
  addedDemand: string[]
  expectedArrivalDate: Date
  nextOrderArrivalDate: Date
  total: number
  children: React.ReactNode
}

export function DemandInPeriodHoverCard({
  entries,
  rawDetailMap,
  addedDemand,
  expectedArrivalDate,
  nextOrderArrivalDate,
  total,
  children,
}: Props) {
  const series = useMemo((): DateChartSeries[] => {
    const allForecasts = entries.flatMap((entry) =>
      [entry.warehouseId, ...addedDemand].flatMap((wh) => {
        const detail = rawDetailMap.get(`${entry.skuId}:${wh}`)
        return detail ? [detail.forecasts] : []
      }),
    )
    const combinedForecasts = padAndSumCumulativeArrays(allForecasts)
    if (!combinedForecasts.length) return []

    const baseDateMs = getStartOfCurrentWeekUTC().getTime()
    const arrivalWeek = (expectedArrivalDate.getTime() - baseDateMs) / MILLISECONDS_IN_WEEK
    const nextArrivalWeek = (nextOrderArrivalDate.getTime() - baseDateMs) / MILLISECONDS_IN_WEEK

    const weekStarts = getStartOfWeekDatesBetween(
      convertToStartOfWeekUTC(expectedArrivalDate),
      nextOrderArrivalDate,
    )
    const data = weekStarts.map((weekStart) => {
      const wStartFrac = (weekStart.getTime() - baseDateMs) / MILLISECONDS_IN_WEEK
      const wStart = Math.max(wStartFrac, arrivalWeek)
      const wEnd = Math.min(wStartFrac + 1, nextArrivalWeek)
      return {
        milliseconds: weekStart.getTime(),
        value: roundForecast(getForecastBetweenWeeks(combinedForecasts, wStart, wEnd)),
      }
    })

    return [
      {
        name: "Demand",
        data,
        type: "column",
        colors: ["rgb(147, 231, 110)"],
      },
    ]
  }, [entries, rawDetailMap, addedDemand, expectedArrivalDate, nextOrderArrivalDate])

  return (
    <HoverCard>
      <HoverCardTrigger className="flex h-full w-full items-center">{children}</HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" align="start">
        <div className="px-4 py-3 border-b border-base bg-base">
          <span className="label-base text-fg-base">Demand in period</span>
          <div className="text-2xl font-semibold mt-1 text-fg-base">
            {formatForecast(total)} units
          </div>
          <p className="paragraph-sm text-fg-subtle mt-1">
            {formatDate(expectedArrivalDate, DateFormatType.DayMonth)} –{" "}
            {formatDate(nextOrderArrivalDate, DateFormatType.DayMonth)}
          </p>
        </div>
        <div className="px-2 py-2">
          <SuperChart
            type={ChartType.TimeSeries}
            dateType={DateType.Weekly}
            data={series}
            height={160}
            noLegend={true}
            margin={[5, 5, 30, 40]}
          />
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
